import React, { useState, useEffect } from 'react';
import { ValidatorForm } from 'react-form-validator-core';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Input, Button, DatePicker } from '../../../Atoms';
import { ErrorIcon } from '../../../Assets';
import { Logo } from '../../../Assets';
import { StepIndicator } from '../../../components';
import {
  setupProfile,
  resetOnboarding,
} from '../../../redux/actions/OnboardingAction';
import { getUserInfo } from '../../../redux/actions/AuthenticationAction';
import { updateUserInfo } from '../../../redux/actions/AuthenticationAction';

import './profile-setup.scss';

const ProfileSetup = ({
  setupProfile,
  profileSetup: { loading, error, success, data },
  resetOnboarding,
  getUserInfo,
  updateUserInfo,
}) => {
  const history = useHistory();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    phone_number: '',
    personal_email: '',
    pronoun: '',
    title: '',
  });
  const [pronounFocused, setPronounFocused] = useState(false);

  const sessionId = localStorage.getItem('onboarding_session_id');


  // Helper function to parse MM-DD-YYYY format back to Date object for display
  const parseDateForDisplay = (dateString) => {
    if (!dateString) return null;

    // Handle MM-DD-YYYY format
    if (dateString.includes('-')) {
      const [month, day, year] = dateString.split('-');
      return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
    }

    // Handle MM/DD/YYYY format
    if (dateString.includes('/')) {
      const [month, day, year] = dateString.split('/');
      return new Date(year, month - 1, day);
    }

    // Fallback to direct Date parsing
    return new Date(dateString);
  };

  useEffect(() => {
    console.log('ProfileSetup mounted, sessionId:', sessionId);
    console.log('Session ID type:', typeof sessionId);
    console.log('Session ID length:', sessionId?.length);
    
    if (!sessionId) {
      console.error('❌ No session ID found, redirecting to company code');
      // Redirect to company code if no session
      history.push('/onboarding/company-code');
      return;
    }

    // Validate session ID format
    if (typeof sessionId !== 'string' || sessionId.length < 10) {
      console.error('❌ Invalid session ID format, redirecting to company code');
      history.push('/onboarding/company-code');
      return;
    }
  }, [sessionId, history]);

  useEffect(() => {
    if (success && data) {
      // Store final tokens and user info
      localStorage.setItem('access', data.access_token);
      localStorage.setItem('refresh', data.refresh_token);
      localStorage.setItem('user_info', JSON.stringify(data.user));

      // Log email update success
      console.log('✅ Profile setup successful!');
      console.log(
        '✅ Email updated from temporary to:',
        data.user?.email || formData.personal_email
      );
      console.log('✅ User data:', data.user);

      // Update authentication state with user data from profile setup
      if (data.user) {
        updateUserInfo(data.user);
      }

      // Navigate to password creation (don't clear session yet)
      history.push('/onboarding/password-setup');
    }
  }, [success, data, history, formData.personal_email, updateUserInfo]);

  const handleSubmit = () => {
    console.log('🔍 ProfileSetup handleSubmit called');
    console.log('🔍 Current sessionId:', sessionId);
    console.log('🔍 SessionId type:', typeof sessionId);
    console.log('🔍 SessionId length:', sessionId?.length);
    console.log('🔍 Form data:', formData);
    
    if (!sessionId) {
      console.error('❌ No session ID found, redirecting to company code');
      history.push('/onboarding/company-code');
      return;
    }

    // Validate session ID format
    if (typeof sessionId !== 'string' || sessionId.length < 10) {
      console.error('❌ Invalid session ID format:', sessionId);
      history.push('/onboarding/company-code');
      return;
    }

    // Check localStorage directly
    const storedSessionId = localStorage.getItem('onboarding_session_id');
    console.log('🔍 Stored session ID from localStorage:', storedSessionId);
    console.log('🔍 Session IDs match:', sessionId === storedSessionId);

    const apiPayload = {
      session_id: sessionId,
      ...formData,
    };
    
    console.log('🚀 Sending API request with payload:', apiPayload);
    console.log('🚀 API endpoint will be called with session_id:', apiPayload.session_id);
    
    setupProfile(apiPayload);
  };

  const handleChange = ({ target: { name, value } }) => {
    // Don't trim email addresses to preserve user input
    if (name === 'personal_email') {
      setFormData({ ...formData, [name]: value });
    } else {
      setFormData({ ...formData, [name]: value.trim() });
    }
  };

  const handleDateChange = ({ target: { value: date } }) => {
    if (date) {
      // Handle different date formats and convert to MM-DD-YYYY format
      let formattedDate;
      if (date instanceof Date) {
        // Convert Date object to MM-DD-YYYY format
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        formattedDate = `${month}-${day}-${year}`;
      } else if (typeof date === 'string') {
        // If it's already a string, try to parse and reformat
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          const year = dateObj.getFullYear();
          formattedDate = `${month}-${day}-${year}`;
        } else {
          // If it's already in the correct format, use it
          formattedDate = date;
        }
      } else {
        // Try to convert to Date object
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          const month = String(dateObj.getMonth() + 1).padStart(2, '0');
          const day = String(dateObj.getDate()).padStart(2, '0');
          const year = dateObj.getFullYear();
          formattedDate = `${month}-${day}-${year}`;
        }
      }

      if (formattedDate) {
        setFormData({ ...formData, date_of_birth: formattedDate });
      }
    }
  };

  const handleCancel = () => {
    // Clear session and redirect to company code
    localStorage.removeItem('onboarding_session_id');
    localStorage.removeItem('company_name');
    localStorage.removeItem('support_email');
    history.push('/onboarding/company-code');
  };

  const pronounOptions = [
    { value: '', title: 'Select pronoun (optional)' },
    { value: 'he/him', title: 'he/him' },
    { value: 'she/her', title: 'she/her' },
    { value: 'they/them', title: 'they/them' },
    { value: 'ze/zir', title: 'ze/zir' },
    { value: 'xe/xem', title: 'xe/xem' },
    { value: 'other', title: 'other' },
    { value: 'prefer-not-to-say', title: 'prefer not to say' },
  ];

  return (
    <div className="profile-setup-wrapper">
      <div className="profile-setup-form">
        <div className="logo-wrapper">
          <Logo />
        </div>
        <StepIndicator currentStep={2} totalSteps={3} />
        
        <div className="heading">Complete Your Profile</div>
        <div className="subheading">
          Welcome to Peak Mind! Let's set up your profile to get started.
        </div>

        {error && (
          <div className="error-wrapper">
            <div>
              <ErrorIcon />
            </div>
            <span>{error}</span>
          </div>
        )}

        <ValidatorForm onSubmit={handleSubmit} className="form">
          <div className="form-section">
            <h3 className="section-title">Personal Information</h3>
            <div className="form-row">
              <Input
                onChange={handleChange}
                value={formData.first_name}
                title="First Name *"
                placeholder="Enter your first name"
                name="first_name"
                validators={['required']}
                errorMessages={['First name is required']}
                formValid={!error}
                style={{ marginBottom: 16 }}
              />
              <Input
                onChange={handleChange}
                value={formData.last_name}
                title="Last Name *"
                placeholder="Enter your last name"
                name="last_name"
                validators={['required']}
                errorMessages={['Last name is required']}
                formValid={!error}
                style={{ marginBottom: 16 }}
              />
            </div>

            <DatePicker
              title="Date of Birth *"
              value={
                formData.date_of_birth
                  ? parseDateForDisplay(formData.date_of_birth)
                  : null
              }
              onChange={handleDateChange}
              placeholder="Select your date of birth"
              validators={['required']}
              errorMessages={['Date of birth is required']}
              formValid={!error}
              style={{ marginBottom: 16 }}
            />
          </div>

          <div className="form-section">
            <h3 className="section-title">Contact Information</h3>
            <Input
              onChange={handleChange}
              value={formData.phone_number}
              title="Phone Number"
              placeholder="Enter your phone number (optional)"
              name="phone_number"
              formValid={!error}
              style={{ marginBottom: 16 }}
            />

            <Input
              onChange={handleChange}
              value={formData.personal_email}
              title="Personal Email *"
              placeholder="Enter your personal email address"
              name="personal_email"
              validators={['required', 'isEmail']}
              errorMessages={[
                'Personal email is required',
                'Please enter a valid email address',
              ]}
              formValid={!error}
              style={{ marginBottom: 16 }}
            />
          </div>

          <div className="form-section">
            <h3 className="section-title">Professional Information</h3>
            <div className="form-row">
              <div className="custom-dropdown-wrapper">
                <div
                  className={`custom-input-3 ${
                    pronounFocused || formData.pronoun ? 'focused' : ''
                  }`}
                >
                  <label>Pronoun</label>
                  <select
                    name="pronoun"
                    value={formData.pronoun}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      setFormData({ ...formData, [name]: value });
                    }}
                    onFocus={() => setPronounFocused(true)}
                    onBlur={() => setPronounFocused(false)}
                    className="custom-select"
                  >
                    {pronounOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <Input
                onChange={handleChange}
                value={formData.title}
                title="Job Title"
                placeholder="Enter your job title (optional)"
                name="title"
                formValid={!error}
                style={{ marginBottom: 16 }}
              />
            </div>
          </div>

          <div className="help-text">
            <p>
              * Required fields. Your information is secure and will only be used to personalize your Peak Mind experience.
            </p>
          </div>

          <div className="action-btn">
            <Button type="button" size="sm" color="grey" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" size="sm" color="blue" loading={loading}>
              Complete Setup
            </Button>
          </div>
        </ValidatorForm>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  profileSetup: state.onboarding.profileSetup,
});

const mapDispatchToProps = (dispatch) => ({
  setupProfile: (details) => {
    dispatch(setupProfile(details));
  },
  resetOnboarding: () => {
    dispatch(resetOnboarding());
  },
  getUserInfo: () => {
    dispatch(getUserInfo());
  },
  updateUserInfo: (userData) => {
    dispatch(updateUserInfo(userData));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ProfileSetup);
